import AvatarGroup from 'tio-ui/components/avatar-group';
import type { AvatarGroupPerson } from 'tio-ui/components/avatar-group';
import Avatar from 'tio-ui/components/avatar';
import Popover from 'tio-ui/components/popover';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import type { Appearance } from '../../services/appearance.ts';

import type SessionContextService from '../../services/session-context.ts';
interface WhosViewingComponentSignature {
  Args: {
    appearances: Appearance[] | Record<string, Appearance>;
  };
  Element: HTMLButtonElement;
}

type ViewerAppearance = Partial<Appearance> & {
  personId?: string;
  name?: string;
};

export default class WhosViewingComponent extends Component<WhosViewingComponentSignature> {
  @service declare sessionContext: SessionContextService;

  get currentPersonId() {
    const id = this.sessionContext?.user?.person?.id;
    return id ? String(id) : null;
  }

  get currentPersonName() {
    const person = this.sessionContext?.user?.person;
    if (!person) return null;
    return person.fullName || [person.firstName, person.lastName].filter(Boolean).join(' ') || null;
  }

  get normalizedAppearances(): ViewerAppearance[] {
    if (!this.args.appearances) {
      return [];
    }

    if (Array.isArray(this.args.appearances)) {
      return this.args.appearances;
    }

    return Object.entries(this.args.appearances).map(([personId, appearance]) => ({
      ...appearance,
      personId: appearance?.personId || personId,
    }));
  }

  get filteredAppearances() {
    return this.normalizedAppearances.filter((appearance) => {
      const personId = String(appearance?.personId || '').trim();
      const name = (appearance?.name || '').trim();

      // Drop placeholder entries that have no usable identity information.
      if (!personId && !name) {
        return false;
      }

      return true;
    });
  }

  get people(): AvatarGroupPerson[] {
    return this.filteredAppearances.map((a) => ({
      name:
        a?.name ||
        (String(a?.personId || '') === this.currentPersonId ? this.currentPersonName : null) ||
        'Unknown User',
    }));
  }

  <template>
    <Popover @placement="top" @triggerMode="hover" @intent="info">
      <:trigger as |t|>
        <button
          type="button"
          class="tio-whos-viewing__trigger inline-flex p-0 m-0 border-0"
          {{t.register}}
          {{t.hoverHandlers}}
          ...attributes
        >
          <AvatarGroup @people={{this.people}} @size="sm" @spacing="normal" />
        </button>
      </:trigger>
      <:body>
        <div class="text-sm">
          <ul>
            {{#each this.people as |p|}}
              <li class="tio-row-tight py-1">
                <Avatar @name={{p.name}} @size="xs" />
                <span>{{p.name}}</span>
              </li>
            {{/each}}
          </ul>
        </div>
      </:body>
    </Popover>
  </template>
}
